/**
 * Care Minutes Data Model
 * Tracks care minutes compliance for Aged Care Act 2024
 * Mandatory: 215 minutes per resident per day (44 RN minutes)
 */

export const createCareMinutes = (data = {}) => {
  return {
    id: data.id || `cm-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    residentId: data.residentId || null,
    facilityId: data.facilityId || null,
    wardId: data.wardId || null,
    
    // Date and Time
    date: data.date || new Date().toISOString().split('T')[0],
    shift: data.shift || null, // 'morning', 'afternoon', 'night'
    
    // Care Minutes by Role
    minutes: {
      rn: data.minutes?.rn || 0, // Registered Nurse minutes
      en: data.minutes?.en || 0, // Enrolled Nurse minutes
      pca: data.minutes?.pca || 0, // Personal Care Assistant minutes
      allied: data.minutes?.allied || 0, // Allied Health minutes
      other: data.minutes?.other || 0 // Other staff minutes
    },
    
    // Total and Targets
    totalMinutes: data.totalMinutes || 0,
    rnMinutes: data.rnMinutes || 0,
    targetMinutes: data.targetMinutes || 215, // Default 215, adjusted by AN-ACC
    targetRNMinutes: data.targetRNMinutes || 44, // Default 44 RN minutes
    
    // AN-ACC Adjustment
    anaccClass: data.anaccClass || null,
    anaccAdjustedTarget: data.anaccAdjustedTarget || 215,
    
    // Compliance Status
    compliant: data.compliant ?? false,
    rnCompliant: data.rnCompliant ?? false,
    complianceGap: data.complianceGap || 0, // Minutes short of target
    rnComplianceGap: data.rnComplianceGap || 0, // RN minutes short of target
    
    // Source of Data
    source: data.source || 'roster', // 'roster', 'manual', 'calculated'
    calculatedFromRoster: data.calculatedFromRoster ?? false,
    
    // Metadata
    recordedBy: data.recordedBy || null,
    recordedAt: data.recordedAt || new Date().toISOString(),
    lastUpdated: data.lastUpdated || new Date().toISOString(),
    notes: data.notes || null
  };
};

/**
 * Calculate care minutes from roster data
 */
export const calculateCareMinutesFromRoster = (rosterEntries, residentId, date) => {
  const minutes = {
    rn: 0,
    en: 0,
    pca: 0,
    allied: 0,
    other: 0
  };
  
  // Filter roster entries for this resident and date
  const relevantEntries = rosterEntries.filter(entry => 
    entry.residentId === residentId && 
    entry.date === date
  );
  
  relevantEntries.forEach(entry => {
    const role = entry.staffRole?.toLowerCase() || '';
    const duration = entry.duration || 0; // Duration in minutes
    
    if (role.includes('rn') || role.includes('registered')) {
      minutes.rn += duration;
    } else if (role.includes('en') || role.includes('enrolled')) {
      minutes.en += duration;
    } else if (role.includes('pca') || role.includes('care') || role.includes('assistant')) {
      minutes.pca += duration;
    } else if (role.includes('allied') || role.includes('therapist')) {
      minutes.allied += duration;
    } else {
      minutes.other += duration;
    }
  });
  
  const totalMinutes = Object.values(minutes).reduce((sum, val) => sum + val, 0);
  const rnMinutes = minutes.rn;
  
  return {
    minutes,
    totalMinutes,
    rnMinutes
  };
};

/**
 * Check compliance against targets
 */
export const checkCompliance = (careMinutes, targetMinutes, targetRNMinutes) => {
  const totalMinutes = careMinutes.totalMinutes || 0;
  const rnMinutes = careMinutes.rnMinutes || 0;
  
  const compliant = totalMinutes >= targetMinutes;
  const rnCompliant = rnMinutes >= targetRNMinutes;
  const complianceGap = Math.max(0, targetMinutes - totalMinutes);
  const rnComplianceGap = Math.max(0, targetRNMinutes - rnMinutes);
  
  return {
    compliant,
    rnCompliant,
    complianceGap,
    rnComplianceGap,
    fullyCompliant: compliant && rnCompliant
  };
};

/**
 * AN-ACC adjusted target calculation
 * Higher AN-ACC classes may have adjusted targets (simplified logic)
 */
export const calculateANACCAdjustedTarget = (anaccClass, baseTarget = 215) => {
  // Simplified: Classes 9-13 may have higher targets
  // In production, use official AN-ACC target adjustment algorithm
  if (anaccClass >= 9 && anaccClass <= 13) {
    return baseTarget + 20; // Higher care needs = more minutes
  } else if (anaccClass >= 6 && anaccClass <= 8) {
    return baseTarget + 10;
  }
  return baseTarget;
};

/**
 * Aggregate care minutes for a period
 */
export const aggregateCareMinutes = (careMinutesArray) => {
  const aggregated = {
    totalMinutes: 0,
    rnMinutes: 0,
    targetMinutes: 0,
    targetRNMinutes: 0,
    days: careMinutesArray.length,
    compliantDays: 0,
    rnCompliantDays: 0,
    fullyCompliantDays: 0
  };
  
  careMinutesArray.forEach(cm => {
    aggregated.totalMinutes += cm.totalMinutes || 0;
    aggregated.rnMinutes += cm.rnMinutes || 0;
    aggregated.targetMinutes += cm.targetMinutes || 0;
    aggregated.targetRNMinutes += cm.targetRNMinutes || 0;
    
    if (cm.compliant) aggregated.compliantDays++;
    if (cm.rnCompliant) aggregated.rnCompliantDays++;
    if (cm.compliant && cm.rnCompliant) aggregated.fullyCompliantDays++;
  });
  
  aggregated.averageMinutes = aggregated.days > 0 ? aggregated.totalMinutes / aggregated.days : 0;
  aggregated.averageRNMinutes = aggregated.days > 0 ? aggregated.rnMinutes / aggregated.days : 0;
  aggregated.complianceRate = aggregated.days > 0 ? (aggregated.fullyCompliantDays / aggregated.days) * 100 : 0;
  
  return aggregated;
};

export default {
  createCareMinutes,
  calculateCareMinutesFromRoster,
  checkCompliance,
  calculateANACCAdjustedTarget,
  aggregateCareMinutes
};
